/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
/**
 * soapParser — routes a free-form clinical dictation into the four SOAP
 * sections (Subjective, Objective, Assessment, Plan) using multilingual
 * keyword triggers (IT / EN / ES).
 *
 * The parser is *stateful across chunks* by design: each call to
 * `feedSoapChunk(state, chunk, lang?)` returns a new immutable state so the
 * caller can maintain a rolling SOAP note as the doctor keeps speaking.
 *
 *   Example (IT):
 *     "Sintomi: mal di testa da tre giorni. Obiettivo: pressione 140 su 90.
 *      Diagnosi: cefalea tensiva. Piano: ibuprofene 400 al bisogno."
 *   →
 *     { subjective: 'mal di testa da tre giorni',
 *       objective:  'pressione 140 su 90',
 *       assessment: 'cefalea tensiva',
 *       plan:       'ibuprofene 400 al bisogno' }
 *
 * If no section header is ever spoken, the parser buckets everything under
 * `subjective` (the safest default — patient's report).
 */

// ---------- Section triggers per language ----------
// Each keyword is a "header": when it appears as the FIRST word of a
// sentence-like fragment (optionally followed by ":" / "—" / "-"), the
// parser flips the active section.
const TRIGGERS = {
  it: {
    subjective: [
      'soggettivo', 'soggettivamente', 'sintomi', 'sintomo',
      'anamnesi', 'riferisce', 'lamenta', 'racconta', 'racconto', 's:',
    ],
    objective: [
      'obiettivo', 'obiettivamente', 'esame obiettivo', 'esame',
      'segni', 'osservazione', 'osservo', 'reperti', 'reperto',
      'parametri', 'ispezione', 'palpazione', 'auscultazione', 'o:',
    ],
    assessment: [
      'valutazione', 'assessment', 'diagnosi', 'ipotesi diagnostica',
      'ipotesi', 'giudizio clinico', 'impressione', 'a:',
    ],
    plan: [
      'piano', 'piano terapeutico', 'terapia', 'trattamento',
      'prescrizione', 'prescrivo', 'prescriviamo', 'follow up',
      'follow-up', 'controllo', 'consiglio', 'consiglia', 'raccomando',
      'raccomandazione', 'p:',
    ],
  },
  en: {
    subjective: [
      'subjective', 'symptoms', 'symptom', 'history',
      'reports', 'complains of', 'presents with', 'chief complaint', 's:',
    ],
    objective: [
      'objective', 'exam', 'examination', 'physical exam', 'physical',
      'findings', 'observation', 'observations', 'vitals', 'vital signs',
      'inspection', 'palpation', 'auscultation', 'o:',
    ],
    assessment: [
      'assessment', 'diagnosis', 'differential', 'impression',
      'clinical impression', 'a:',
    ],
    plan: [
      'plan', 'treatment', 'treatment plan', 'prescribe', 'prescribing',
      'recommend', 'recommendation', 'follow up', 'follow-up',
      'management', 'p:',
    ],
  },
  es: {
    subjective: [
      'subjetivo', 'subjetivamente', 'síntomas', 'sintomas', 'síntoma', 'sintoma',
      'anamnesis', 'refiere', 'presenta', 'motivo de consulta', 's:',
    ],
    objective: [
      'objetivo', 'objetivamente', 'exploración', 'exploracion', 'examen',
      'examen físico', 'signos', 'observación', 'observacion', 'hallazgos',
      'inspección', 'palpación', 'auscultación', 'o:',
    ],
    assessment: [
      'evaluación', 'evaluacion', 'diagnóstico', 'diagnostico',
      'impresión', 'impresion', 'impresión clínica', 'juicio clínico', 'a:',
    ],
    plan: [
      'plan', 'tratamiento', 'plan terapéutico', 'prescribo', 'prescripción',
      'recomendación', 'recomiendo', 'seguimiento', 'control', 'p:',
    ],
  },
};

const SECTION_KEYS = ['subjective', 'objective', 'assessment', 'plan'];

// Trigger words that are ALSO meaningful inside the sentence (verbs / nouns
// in natural speech). They switch the section but are KEPT in the text so the
// note stays grammatical: "riferisce cefalea" → S: "riferisce cefalea".
const KEEP_IN_TEXT = new Set([
  'riferisce', 'lamenta', 'racconta', 'racconto', 'controllo', 'consiglio', 'consiglia',
  'raccomando', 'raccomandazione', 'prescrivo', 'prescriviamo', 'prescrizione', 'terapia', 'trattamento',
  'osservo', 'osservazione', 'parametri', 'segni', 'reperti', 'reperto', 'esame',
  'reports', 'complains of', 'presents with', 'recommend', 'recommendation', 'prescribe', 'prescribing', 'treatment', 'management',
  'refiere', 'presenta', 'recomiendo', 'recomendación', 'prescribo', 'tratamiento', 'control', 'seguimiento',
]);

// Multilingual auto-detection re-uses the same heuristics as dictationParser
// but on a lighter set of hints — plus every trigger word counts too.
const LANG_HINT_TOKENS = {
  it: ['sintomi', 'obiettivo', 'diagnosi', 'piano', 'terapia', 'riferisce', 'prescrivo'],
  en: ['symptoms', 'exam', 'diagnosis', 'plan', 'treatment', 'reports', 'prescribe'],
  es: ['síntomas', 'sintomas', 'examen', 'diagnóstico', 'plan', 'tratamiento', 'refiere'],
};

export const detectSoapLang = (text) => {
  const scores = { it: 0, en: 0, es: 0 };
  const lower = ` ${(text || '').toLowerCase()} `;
  for (const [lang, hints] of Object.entries(LANG_HINT_TOKENS)) {
    for (const h of hints) {
      if (lower.includes(` ${h} `) || lower.includes(` ${h}.`) || lower.includes(` ${h},`) || lower.includes(` ${h}:`)) {
        scores[lang] += 1;
      }
    }
  }
  const best = Object.entries(scores).sort((a, b) => b[1] - a[1])[0];
  return best[1] > 0 ? best[0] : null;
};

/**
 * Split a raw chunk into fragments at each new SOAP header.
 *
 * Every fragment has the form { section, text }. The parser preserves the
 * incoming order so the caller can append them to the running state.
 */
const splitBySections = (chunk, lang) => {
  const dict = TRIGGERS[lang] || TRIGGERS.it;
  // Build a single regex like /\b(sintomi|obiettivo|...|p:)\b\s*[:—-]?\s*/gi.
  // Escape special chars (e.g. ':' or spaces inside "chief complaint").
  const all = SECTION_KEYS.flatMap((sk) => (dict[sk] || []).map((k) => ({ section: sk, kw: k })));
  // Sort by descending length so multi-word triggers match first.
  all.sort((a, b) => b.kw.length - a.kw.length);
  const escaped = all.map(({ kw }) => kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const pattern = new RegExp(`(^|[\\.!?\\n]|\\s)(${escaped.join('|')})\\b\\s*[:—-]?\\s*`, 'gi');

  const fragments = [];
  let cursor = 0;
  let currentSection = null;
  let m;
  while ((m = pattern.exec(chunk)) !== null) {
    const before = chunk.slice(cursor, m.index).trim();
    if (before) fragments.push({ section: currentSection, text: before });
    const kwLower = m[2].toLowerCase();
    const found = all.find((a) => a.kw.toLowerCase() === kwLower);
    currentSection = found ? found.section : currentSection;
    // Keep natural-language triggers inside the fragment text.
    cursor = KEEP_IN_TEXT.has(kwLower) ? m.index + m[1].length : pattern.lastIndex;
  }
  const tail = chunk.slice(cursor).trim();
  if (tail) fragments.push({ section: currentSection, text: tail });
  return fragments;
};

/**
 * Immutable append helper — never mutates the incoming state.
 */
const appendText = (existing, addition) => {
  const a = (existing || '').trim();
  const b = (addition || '').trim();
  if (!b) return a;
  if (!a) return b;
  return `${a} ${b}`;
};

/**
 * Feed one dictation chunk into the SOAP state machine.
 *
 * @param {Object} state — previous SOAP state (or `emptySoapState()`)
 * @param {string} chunk — the newly transcribed text
 * @param {Object} [opts]
 * @param {'it'|'en'|'es'|'auto'} [opts.lang='auto']
 * @returns {{state, changedSections: string[], lang: string}}
 */
export const feedSoapChunk = (state, chunk, opts = {}) => {
  const cleaned = (chunk || '').replace(/\s+/g, ' ').trim();
  if (!cleaned) return { state, changedSections: [], lang: opts.lang || 'it' };

  const lang = opts.lang && opts.lang !== 'auto'
    ? opts.lang
    : (detectSoapLang(cleaned) || state.lastLang || 'it');

  const fragments = splitBySections(cleaned, lang);
  const next = { ...state, lastLang: lang };
  const changed = new Set();

  // Track the "sticky" section — if a chunk starts without a header, it
  // continues where the previous chunk left off.
  let stickySection = state.lastSection || 'subjective';
  for (const frag of fragments) {
    const target = frag.section || stickySection;
    if (!target) continue;
    next[target] = appendText(next[target], frag.text);
    changed.add(target);
    if (frag.section) stickySection = frag.section;
  }
  next.lastSection = stickySection;
  return { state: next, changedSections: [...changed], lang };
};

export const emptySoapState = () => ({
  subjective: '',
  objective: '',
  assessment: '',
  plan: '',
  lastSection: null,
  lastLang: null,
});

/**
 * Format the current state as a human-readable SOAP block, suitable for the
 * `notes` textarea of the clinical form.
 */
export const formatSoapAsText = (state) => {
  const parts = [];
  if (state.subjective) parts.push(`S: ${state.subjective}`);
  if (state.objective) parts.push(`O: ${state.objective}`);
  if (state.assessment) parts.push(`A: ${state.assessment}`);
  if (state.plan) parts.push(`P: ${state.plan}`);
  return parts.join('\n');
};

export default feedSoapChunk;
